import { useEffect, useMemo, useRef, useState } from "react";
import { forceCenter, forceCollide, forceLink, forceManyBody, forceSimulation, forceX, forceY, type SimulationLinkDatum, type SimulationNodeDatum } from "d3-force";
import { quadtree } from "d3-quadtree";
import { RotateCcw, SlidersHorizontal, X } from "lucide-react";
import type { GraphPayload, GraphNode } from "./types";

interface SimNode extends SimulationNodeDatum, GraphNode {
  radius: number;
}

interface SimLink extends SimulationLinkDatum<SimNode> {
  source: SimNode;
  target: SimNode;
}

interface Transform { x: number; y: number; k: number }

const savedPositions = new Map<string, { x: number; y: number }>();
let savedTransform: Transform = { x: 0, y: 0, k: 1 };
let hasUserTransform = false;
// App.tsx размонтирует GraphCanvas при переходе на заметку и монтирует заново при возврате
// к графу, поэтому обычный useState фильтра сбрасывался бы вместе с компонентом — как и
// позицию камеры, состояние фильтра храним в модульной переменной, переживающей remount.
let savedFiltersOpen = false;
let savedEnabledTypes: Record<string, boolean> = {};

// Симуляция никогда не замирает полностью: на этом alpha ноды продолжают медленно плавать, как в Obsidian.
const IDLE_ALPHA = 0.012;
const DRIFT_SPEED = 0.42;
const DRIFT_AMPLITUDE = 0.1;
// Возврат к «дому» удерживает покачивание на месте, иначе ноды медленно уползают по графу.
const HOME_PULL = 0.02;
const HOME_FOLLOW = 0.002;
// Три пояса по тяге к центру, от слабейшего к сильнейшему: обычное ядро, хвосты внутри главной
// компоненты (степень 1-2 — держатся только связью с соседом, а отталкивание от плотного ядра
// выдувает их наружу) и полностью несвязанные ноды/мелкие острова вне главной компоненты.
// Хвостам тяга сильнее, чем одиночкам, — тогда одиночки оседают чуть дальше хвостов, а не глубже
// в кластере, но сами хвосты всё равно подтягиваются и не торчат так далеко, как раньше.
const ISOLATED_CHARGE_FACTOR = 0.4;
const PENDANT_DEGREE_MAX = 2;
const PENDANT_PULL_MULTIPLIER = 4.5;
const OUTSIDER_PULL_MULTIPLIER = 3;
// Радиус поводка растёт с размером главной компоненты (иначе крупный vault упёрся бы в потолок,
// рассчитанный на нынешние ~250 нод), а сила реагирует на превышение постепенно, не рывком.
const LEASH_RADIUS_PER_SQRT_NODE = 15.5;
const LEASH_STRENGTH = 0.1;
// Подвешенные ветви получают дополнительный возврат: так низкостепенная нода внутри
// маленькой подгруппы не образует длинный хвост, а плотное ядро не становится шаром.
const TAIL_LEASH_MULTIPLIER = 2.6;
// Минимальный зазор между кругами: столкновения с этим радиусом полностью исключают наложение.
const NODE_GAP = 5;
// Плотность укладки: доля от Obsidian-настройки linkDistance/repelStrength, которую реально
// применяет симуляция. Меньше значение — теснее кластер.
const LINK_DISTANCE_SCALE = 0.34;
const LINK_DISTANCE_MIN = 26;
const CHARGE_SCALE = 3.6;
// Множитель тяги к центру для нод главной компоненты. Без него редко связанные хвосты-цепочки
// удерживает только их собственная связь с соседом, а взаимное отталкивание разносит их далеко
// от ядра; более сильная тяга подтягивает такие ветви ближе, сохраняя их отдельными «рукавами».
const CENTER_PULL_SCALE = 0.1;
// Размер круга ноды и подписи под ним.
const NODE_RADIUS_MIN = 2.2;
const NODE_RADIUS_MAX = 11;
const NODE_RADIUS_SCALE = 2.1;
const LABEL_FONT_SIZE = 9;
// Связи почти не видны в состоянии покоя и лишь немного ярче для соседей выбранной ноды.
const LINK_ALPHA_FOCUSED = 0.16;
const LINK_ALPHA_DIMMED = 0.03;
// Подписи начинают проявляться, когда зум превышает обзорный масштаб в LABEL_ZOOM_START раз,
// и достигают полной яркости ещё через LABEL_FADE_SPAN. Порог задан относительно обзорного
// масштаба, а не абсолютного зума, поэтому не зависит от размера графа и окна.
const LABEL_ZOOM_START = 1.5;
const LABEL_FADE_SPAN = 0.75;
// Доля оставшегося пути до целевого масштаба, проходимая за кадр при 60 fps.
// Чем меньше, тем длиннее и мягче глиссада: 0.11 — примерно треть секунды до цели.
const ZOOM_EASE = 0.11;
const ZOOM_SENSITIVITY = 0.0016;

const TYPE_LABELS: Record<string, string> = {
  character: "Персонажи",
  object: "Объекты",
  location: "Локации",
  community: "Сообщества",
  race: "Сообщества",
  canon: "Канон",
  unknown: "Без типа"
};

const TYPE_COLORS: Record<string, string> = {
  character: "#4caf50",
  object: "#ff9800",
  location: "#2196f3",
  community: "#a88bda",
  race: "#a88bda",
  canon: "#8b8b96",
  unknown: "#8b8b96"
};

function typeKey(node: GraphNode): string {
  return node.type?.trim().toLocaleLowerCase("ru-RU") || "unknown";
}

function typeLabel(type: string): string {
  return TYPE_LABELS[type] ?? type.charAt(0).toLocaleUpperCase("ru-RU") + type.slice(1);
}

function typeColor(type: string): string {
  return TYPE_COLORS[type] ?? "#8b8b96";
}

export function GraphCanvas({ graph, onOpen }: { graph: GraphPayload; onOpen: (id: string) => void }) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const stateRef = useRef<{ nodes: SimNode[]; links: SimLink[]; transform: Transform; hovered?: SimNode; selected?: SimNode }>({ nodes: [], links: [], transform: savedTransform });
  const [filtersOpen, setFiltersOpenState] = useState(savedFiltersOpen);
  const [enabledTypes, setEnabledTypesState] = useState<Record<string, boolean>>(savedEnabledTypes);
  const setFiltersOpen = (value: boolean): void => {
    savedFiltersOpen = value;
    setFiltersOpenState(value);
  };
  const setEnabledTypes = (updater: Record<string, boolean> | ((current: Record<string, boolean>) => Record<string, boolean>)): void => {
    savedEnabledTypes = typeof updater === "function" ? updater(savedEnabledTypes) : updater;
    setEnabledTypesState(savedEnabledTypes);
  };
  const reducedMotion = useMemo(() => window.matchMedia("(prefers-reduced-motion: reduce)").matches, []);
  const typeOptions = useMemo(() => {
    const types = new Set(graph.nodes.map(typeKey));
    return [...types].sort((first, second) => typeLabel(first).localeCompare(typeLabel(second), "ru"));
  }, [graph.nodes]);
  const visibleTypes = useMemo(() => new Set(typeOptions.filter((type) => enabledTypes[type] !== false)), [enabledTypes, typeOptions]);
  const visibleGraph = useMemo<GraphPayload>(() => {
    const nodes = graph.nodes.filter((node) => visibleTypes.has(typeKey(node)));
    const filteredIndex = new Map<number, number>();
    graph.nodes.forEach((node, index) => {
      if (visibleTypes.has(typeKey(node))) filteredIndex.set(index, filteredIndex.size);
    });
    const links: Array<[number, number]> = [];
    for (const [source, target] of graph.links) {
      const nextSource = filteredIndex.get(source);
      const nextTarget = filteredIndex.get(target);
      if (nextSource !== undefined && nextTarget !== undefined) links.push([nextSource, nextTarget]);
    }
    return { ...graph, nodes, links };
  }, [graph, visibleTypes]);
  const hiddenTypeCount = typeOptions.filter((type) => !visibleTypes.has(type)).length;
  const preserveCameraBeforeFilterChange = (): void => {
    // Фильтр меняет только состав нод. Камера должна остаться на прежнем
    // масштабе и месте, даже если новый подграф заметно меньше исходного.
    savedTransform = { ...stateRef.current.transform };
    hasUserTransform = true;
  };
  const toggleType = (type: string): void => {
    preserveCameraBeforeFilterChange();
    setEnabledTypes((current) => ({ ...current, [type]: current[type] === false }));
  };
  const resetTypes = (): void => {
    preserveCameraBeforeFilterChange();
    setEnabledTypes({});
  };

  useEffect(() => {
    const graph = visibleGraph;
    const target = canvasRef.current;
    if (!target) return;
    const targetContext = target.getContext("2d");
    if (!targetContext) return;
    const canvas: HTMLCanvasElement = target;
    const context: CanvasRenderingContext2D = targetContext;
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    const resize = () => {
      const box = canvas.getBoundingClientRect();
      canvas.width = Math.max(1, Math.floor(box.width * dpr));
      canvas.height = Math.max(1, Math.floor(box.height * dpr));
      context.setTransform(dpr, 0, 0, dpr, 0, 0);
      // Первый вызов приходит до раскладки, когда канвас ещё нулевого размера, поэтому обзорный
      // масштаб пересчитывается здесь: иначе граф открывается на нижнем зажиме 0.16, то есть
      // крошечным, а порог подписей считается от неверной базы.
      fitToView();
      draw();
    };

    const nodes: SimNode[] = graph.nodes.map((node, index) => {
      const saved = savedPositions.get(node.id);
      const angle = index * 2.399963;
      const distance = 16 * Math.sqrt(index + 1);
      return {
        ...node,
        radius: Math.min(NODE_RADIUS_MAX, Math.max(NODE_RADIUS_MIN, NODE_RADIUS_SCALE * graph.settings.nodeSizeMultiplier * (1 + Math.sqrt(node.degree) * 0.25))),
        x: saved?.x ?? Math.cos(angle) * distance,
        y: saved?.y ?? Math.sin(angle) * distance
      };
    });
    const links: SimLink[] = graph.links.map(([source, target]) => ({ source: nodes[source]!, target: nodes[target]! }));
    stateRef.current = { ...stateRef.current, nodes, links, hovered: undefined, selected: undefined };
    // Компоненты связности через систему непересекающихся множеств. Всё, что не входит в главную
    // компоненту — одиночки, пары, мелкие острова — держим у кластера: без этого их уносит на
    // периферию, потому что отталкивание есть, а притягивающих связей с ядром нет.
    const parent = nodes.map((_, index) => index);
    const findRoot = (index: number): number => {
      let current = index;
      while (parent[current] !== current) {
        parent[current] = parent[parent[current]!]!;
        current = parent[current]!;
      }
      return current;
    };
    const indexOfNode = new Map(nodes.map((node, index) => [node, index]));
    for (const link of links) {
      const first = findRoot(indexOfNode.get(link.source)!);
      const second = findRoot(indexOfNode.get(link.target)!);
      if (first !== second) parent[first] = second;
    }
    const componentSize = new Map<number, number>();
    for (let index = 0; index < nodes.length; index += 1) {
      const root = findRoot(index);
      componentSize.set(root, (componentSize.get(root) ?? 0) + 1);
    }
    let mainComponent = -1;
    let mainSize = 0;
    for (const [root, size] of componentSize) if (size > mainSize) { mainSize = size; mainComponent = root; }
    const outsiders = new Set(nodes.filter((_, index) => findRoot(index) !== mainComponent));
    // Хвост считается от его конца: любая нода степени <=2 внутри главной компоненты, если
    // отталкивание не сдержит только цепочка их обратно к хабу связей. Внешние острова уже
    // покрыты набором outsiders, здесь добавляются только "рукава" внутри главной компоненты.
    const pendants = new Set(nodes.filter((node) => node.degree > 0 && node.degree <= PENDANT_DEGREE_MAX && !outsiders.has(node)));

    const charge = -Math.max(18, graph.settings.repelStrength * CHARGE_SCALE);
    const centerPull = Math.max(0.004, graph.settings.centerStrength * CENTER_PULL_SCALE);
    const pendantPull = centerPull * PENDANT_PULL_MULTIPLIER;
    const outsiderPull = centerPull * OUTSIDER_PULL_MULTIPLIER;
    const pullFor = (node: SimNode): number => (pendants.has(node) ? pendantPull : outsiders.has(node) ? outsiderPull : centerPull);
    // Хвосты — не всегда прямая цепочка: внутри маленькой подвешенной группы может найтись своя
    // нода со степенью 3, которую правило pendants не поймает. Поэтому вдобавок к целевым тягам
    // держим мягкий поводок: всё, что после укладки всё равно оказалось дальше LEASH_RADIUS от
    // центра, получает возврат пропорционально превышению — какой бы ни была форма хвоста.
    const leashRadius = LEASH_RADIUS_PER_SQRT_NODE * Math.sqrt(mainSize);
    function leash(): void {
      for (const node of nodes) {
        if (outsiders.has(node)) continue;
        const x = node.x ?? 0;
        const y = node.y ?? 0;
        const distance = Math.hypot(x, y);
        if (distance <= leashRadius) continue;
        const tailBoost = node.degree <= 3 ? TAIL_LEASH_MULTIPLIER : 1;
        const pull = (LEASH_STRENGTH * tailBoost * (distance - leashRadius)) / distance;
        node.vx = (node.vx ?? 0) - x * pull;
        node.vy = (node.vy ?? 0) - y * pull;
      }
    }
    const simulation = forceSimulation(nodes)
      .force("charge", forceManyBody<SimNode>().strength((node) => charge * (outsiders.has(node) || pendants.has(node) ? ISOLATED_CHARGE_FACTOR : 1)).theta(0.9))
      .force("link", forceLink<SimNode, SimLink>(links).distance(Math.max(LINK_DISTANCE_MIN, graph.settings.linkDistance * LINK_DISTANCE_SCALE)).strength(Math.min(1, graph.settings.linkStrength)))
      .force("center", forceCenter(0, 0).strength(Math.min(1, graph.settings.centerStrength)))
      .force("x", forceX<SimNode>(0).strength(pullFor))
      .force("y", forceY<SimNode>(0).strength(pullFor))
      .force("leash", leash)
      .force("collision", forceCollide<SimNode>().radius((node) => node.radius + NODE_GAP).strength(1).iterations(2))
      .velocityDecay(0.42)
      .alphaDecay(0.025)
      .on("tick", () => {
        for (const node of nodes) if (node.x !== undefined && node.y !== undefined) savedPositions.set(node.id, { x: node.x, y: node.y });
        draw();
      });

    // Лёгкое покачивание: каждой ноде даётся собственная фаза, поэтому кластер дышит, а не дрейфует целиком.
    // «Дом» медленно следует за нодой, так что укладка по-прежнему может смещаться, а вот само
    // покачивание остаётся ограниченным несколькими пикселями вокруг текущей позиции.
    const home = nodes.map((node) => ({ x: node.x ?? 0, y: node.y ?? 0 }));
    function seedHome(): void {
      for (let index = 0; index < nodes.length; index += 1) home[index] = { x: nodes[index]!.x ?? 0, y: nodes[index]!.y ?? 0 };
    }
    function drift(): void {
      const time = performance.now() / 1000;
      for (let index = 0; index < nodes.length; index += 1) {
        const node = nodes[index]!;
        if (node.fx !== null && node.fx !== undefined) continue;
        const anchor = home[index]!;
        anchor.x += ((node.x ?? 0) - anchor.x) * HOME_FOLLOW;
        anchor.y += ((node.y ?? 0) - anchor.y) * HOME_FOLLOW;
        const phase = index * 2.399963;
        node.vx = (node.vx ?? 0) + Math.cos(time * DRIFT_SPEED + phase) * DRIFT_AMPLITUDE + (anchor.x - (node.x ?? 0)) * HOME_PULL;
        node.vy = (node.vy ?? 0) + Math.sin(time * DRIFT_SPEED * 0.87 + phase * 1.3) * DRIFT_AMPLITUDE + (anchor.y - (node.y ?? 0)) * HOME_PULL;
      }
    }

    // Обзорный масштаб графа; заполняется fitToView до первой отрисовки.
    let baseScale = 0.5;
    function labelOpacity(k: number): number {
      const start = baseScale * LABEL_ZOOM_START * Math.pow(0.78, graph.settings.textFadeMultiplier);
      const full = start * (1 + LABEL_FADE_SPAN);
      return Math.max(0, Math.min(0.92, (k - start) / (full - start)));
    }

    function color(node: SimNode): string {
      return node.group !== undefined ? graph.groups[node.group]?.color ?? "#8b8b96" : "#8b8b96";
    }

    function neighbors(node: SimNode): Set<SimNode> {
      const result = new Set<SimNode>([node]);
      for (const link of links) {
        if (link.source === node) result.add(link.target);
        if (link.target === node) result.add(link.source);
      }
      return result;
    }

    function draw(): void {
      const box = canvas.getBoundingClientRect();
      context.clearRect(0, 0, box.width, box.height);
      const { transform, hovered, selected } = stateRef.current;
      const focus = hovered ?? selected;
      const neighborhood = focus ? neighbors(focus) : undefined;
      context.save();
      context.translate(box.width / 2 + transform.x, box.height / 2 + transform.y);
      context.scale(transform.k, transform.k);

      for (const link of links) {
        const active = !focus || neighborhood?.has(link.source) && neighborhood.has(link.target);
        context.globalAlpha = active ? LINK_ALPHA_FOCUSED : LINK_ALPHA_DIMMED;
        context.strokeStyle = active && focus ? "#a994d6" : "#65656b";
        context.lineWidth = (active && focus ? 1.25 : 0.72) * graph.settings.lineSizeMultiplier / transform.k ** 0.18;
        context.beginPath();
        context.moveTo(link.source.x ?? 0, link.source.y ?? 0);
        context.lineTo(link.target.x ?? 0, link.target.y ?? 0);
        context.stroke();
      }

      for (const node of nodes) {
        const active = !focus || neighborhood?.has(node);
        const highlighted = node === focus || node === selected;
        context.globalAlpha = active ? 1 : 0.2;
        context.shadowBlur = highlighted ? 14 / transform.k : 0;
        context.shadowColor = color(node);
        context.fillStyle = color(node);
        context.beginPath();
        context.arc(node.x ?? 0, node.y ?? 0, node.radius * (highlighted ? 1.25 : 1), 0, Math.PI * 2);
        context.fill();
        context.shadowBlur = 0;
        const labelAlpha = highlighted ? 1 : labelOpacity(transform.k);
        if (labelAlpha > 0.04) {
          context.globalAlpha = (active ? 1 : 0.18) * labelAlpha;
          context.fillStyle = "#dcddde";
          context.font = `${highlighted ? 600 : 450} ${LABEL_FONT_SIZE / transform.k}px system-ui, sans-serif`;
          context.textAlign = "center";
          context.fillText(node.title, node.x ?? 0, (node.y ?? 0) + node.radius + 12 / transform.k);
        }
      }
      context.restore();
      context.globalAlpha = 1;
    }

    function fitToView(): void {
      if (!nodes.length) return;
      const box = canvas.getBoundingClientRect();
      if (box.width < 1 || box.height < 1) return;
      const xs = nodes.map((node) => node.x ?? 0);
      const ys = nodes.map((node) => node.y ?? 0);
      const minX = Math.min(...xs);
      const maxX = Math.max(...xs);
      const minY = Math.min(...ys);
      const maxY = Math.max(...ys);
      const scale = Math.max(0.16, Math.min(1.2, 0.82 * Math.min(box.width / Math.max(1, maxX - minX), box.height / Math.max(1, maxY - minY))));
      // Обзорный масштаб нужен и для порога подписей, поэтому считается всегда,
      // даже когда собственный масштаб пользователя менять нельзя.
      baseScale = scale;
      if (hasUserTransform) return;
      stateRef.current.transform = { x: -((minX + maxX) / 2) * scale, y: -((minY + maxY) / 2) * scale, k: scale };
      savedTransform = { ...stateRef.current.transform };
    }

    simulation.stop();
    for (let index = 0; index < (reducedMotion ? 240 : 130); index += 1) simulation.tick();
    fitToView();
    draw();
    if (!reducedMotion) {
      // Дрейф включается только после укладки, иначе он смещает стартовые позиции.
      seedHome();
      simulation.force("drift", drift);
      simulation.alphaTarget(IDLE_ALPHA).alpha(0.28).restart();
    }

    const observer = new ResizeObserver(resize);
    observer.observe(canvas);
    resize();

    let pointerId: number | undefined;
    let start = { x: 0, y: 0 };
    let previous = { x: 0, y: 0 };
    let draggedNode: SimNode | undefined;
    let moved = false;
    let previousTap: string | undefined;
    const clearSelection = (): boolean => {
      if (!stateRef.current.selected && previousTap === undefined) return false;
      stateRef.current.selected = undefined;
      previousTap = undefined;
      return true;
    };
    let zoomTarget: number | undefined;
    let zoomAnchor: { worldX: number; worldY: number; screenX: number; screenY: number } | undefined;
    let zoomFrame: number | undefined;
    let zoomLastFrame = 0;
    const cancelZoom = (): void => {
      if (zoomFrame !== undefined) cancelAnimationFrame(zoomFrame);
      zoomFrame = undefined;
      zoomTarget = undefined;
      zoomAnchor = undefined;
    };
    const activePointers = new Map<number, { x: number; y: number }>();
    let pinch: { distance: number; world: { x: number; y: number }; startTransform: Transform } | undefined;

    const screenToWorld = (x: number, y: number) => {
      const box = canvas.getBoundingClientRect();
      const t = stateRef.current.transform;
      return { x: (x - box.left - box.width / 2 - t.x) / t.k, y: (y - box.top - box.height / 2 - t.y) / t.k };
    };
    const hit = (x: number, y: number) => {
      const world = screenToWorld(x, y);
      const tree = quadtree<SimNode>().x((node) => node.x ?? 0).y((node) => node.y ?? 0).addAll(nodes);
      return tree.find(world.x, world.y, 18 / stateRef.current.transform.k);
    };
    const onPointerDown = (event: PointerEvent) => {
      // Перетаскивание и щипок задают масштаб напрямую, поэтому доводку колеса надо прервать.
      cancelZoom();
      activePointers.set(event.pointerId, { x: event.clientX, y: event.clientY });
      pointerId = event.pointerId;
      canvas.setPointerCapture(pointerId);
      if (activePointers.size === 2) {
        const [first, second] = [...activePointers.values()];
        if (!first || !second) return;
        const center = { x: (first.x + second.x) / 2, y: (first.y + second.y) / 2 };
        pinch = {
          distance: Math.max(1, Math.hypot(second.x - first.x, second.y - first.y)),
          world: screenToWorld(center.x, center.y),
          startTransform: { ...stateRef.current.transform }
        };
        if (draggedNode) {
          draggedNode.fx = null;
          draggedNode.fy = null;
          draggedNode = undefined;
        }
        if (clearSelection()) draw();
        moved = true;
        return;
      }
      start = previous = { x: event.clientX, y: event.clientY };
      draggedNode = hit(event.clientX, event.clientY);
      moved = false;
      if (draggedNode) {
        draggedNode.fx = draggedNode.x;
        draggedNode.fy = draggedNode.y;
        simulation.alphaTarget(0.15).restart();
      }
    };
    const onPointerMove = (event: PointerEvent) => {
      if (activePointers.has(event.pointerId)) activePointers.set(event.pointerId, { x: event.clientX, y: event.clientY });
      if (activePointers.size >= 2 && pinch) {
        const [first, second] = [...activePointers.values()];
        if (!first || !second) return;
        const center = { x: (first.x + second.x) / 2, y: (first.y + second.y) / 2 };
        const distance = Math.max(1, Math.hypot(second.x - first.x, second.y - first.y));
        const nextK = Math.max(0.16, Math.min(6, pinch.startTransform.k * distance / pinch.distance));
        const box = canvas.getBoundingClientRect();
        const transform = stateRef.current.transform;
        transform.x = center.x - box.left - box.width / 2 - pinch.world.x * nextK;
        transform.y = center.y - box.top - box.height / 2 - pinch.world.y * nextK;
        transform.k = nextK;
        savedTransform = { ...transform };
        hasUserTransform = true;
        draw();
        return;
      }
      if (pointerId === event.pointerId) {
        const dx = event.clientX - previous.x;
        const dy = event.clientY - previous.y;
        if (Math.hypot(event.clientX - start.x, event.clientY - start.y) > 4) moved = true;
        if (draggedNode) {
          const world = screenToWorld(event.clientX, event.clientY);
          draggedNode.fx = world.x;
          draggedNode.fy = world.y;
        } else {
          // Движение камеры завершает контекст выбранной ноды. Ждём порога drag,
          // чтобы дрожание пальца при обычном тапе не снимало выделение.
          if (moved) clearSelection();
          stateRef.current.transform.x += dx;
          stateRef.current.transform.y += dy;
          savedTransform = { ...stateRef.current.transform };
          hasUserTransform = true;
          draw();
        }
        previous = { x: event.clientX, y: event.clientY };
      } else {
        stateRef.current.hovered = hit(event.clientX, event.clientY);
        canvas.style.cursor = stateRef.current.hovered ? "pointer" : "grab";
        draw();
      }
    };
    const onPointerUp = (event: PointerEvent) => {
      const wasPinching = activePointers.size >= 2;
      activePointers.delete(event.pointerId);
      if (wasPinching) {
        pinch = undefined;
        const remaining = [...activePointers.entries()][0];
        if (remaining) {
          pointerId = remaining[0];
          start = previous = { ...remaining[1] };
          moved = true;
        } else {
          pointerId = undefined;
        }
        return;
      }
      if (pointerId !== event.pointerId) return;
      if (draggedNode) {
        draggedNode.fx = null;
        draggedNode.fy = null;
        simulation.alphaTarget(reducedMotion ? 0 : IDLE_ALPHA).alpha(0.28).restart();
      }
      if (!moved) {
        const node = hit(event.clientX, event.clientY);
        stateRef.current.selected = node;
        draw();
        // Мышью и пером нода открывается одним кликом. На тач-экране нет hover, поэтому
        // первое касание показывает имя, а открывает уже повторное по той же ноде.
        if (node && (event.pointerType !== "touch" || previousTap === node.id)) onOpen(node.id);
        previousTap = node?.id;
      }
      pointerId = undefined;
      draggedNode = undefined;
    };
    // Колесо задаёт целевой масштаб, а текущий подтягивается к нему по кадрам. Точка под курсором
    // остаётся неподвижной на всём протяжении анимации, поэтому x/y пересчитываются от неё, а не
    // интерполируются отдельно. Соседние щелчки колеса накапливаются на цели, а не на текущем k,
    // иначе быстрая прокрутка снова превращалась бы в рывки.
    const applyAnchoredTransform = (k: number): void => {
      if (!zoomAnchor) return;
      const box = canvas.getBoundingClientRect();
      const t = stateRef.current.transform;
      t.k = k;
      t.x = zoomAnchor.screenX - box.left - box.width / 2 - zoomAnchor.worldX * k;
      t.y = zoomAnchor.screenY - box.top - box.height / 2 - zoomAnchor.worldY * k;
      savedTransform = { ...t };
      hasUserTransform = true;
      draw();
    };
    const stepZoom = (time: number): void => {
      zoomFrame = undefined;
      if (zoomTarget === undefined || !zoomAnchor) return;
      const elapsed = Math.min(64, Math.max(1, time - zoomLastFrame));
      zoomLastFrame = time;
      const current = stateRef.current.transform.k;
      const eased = current + (zoomTarget - current) * (1 - Math.pow(1 - ZOOM_EASE, elapsed / 16.667));
      if (Math.abs(zoomTarget - eased) < zoomTarget * 0.0015) {
        applyAnchoredTransform(zoomTarget);
        zoomTarget = undefined;
        zoomAnchor = undefined;
        return;
      }
      applyAnchoredTransform(eased);
      zoomFrame = requestAnimationFrame(stepZoom);
    };
    const onWheel = (event: WheelEvent) => {
      event.preventDefault();
      if (clearSelection()) draw();
      const box = canvas.getBoundingClientRect();
      const t = stateRef.current.transform;
      const baseK = zoomTarget ?? t.k;
      const baseX = zoomTarget !== undefined && zoomAnchor ? zoomAnchor.screenX - box.left - box.width / 2 - zoomAnchor.worldX * baseK : t.x;
      const baseY = zoomTarget !== undefined && zoomAnchor ? zoomAnchor.screenY - box.top - box.height / 2 - zoomAnchor.worldY * baseK : t.y;
      zoomAnchor = {
        worldX: (event.clientX - box.left - box.width / 2 - baseX) / baseK,
        worldY: (event.clientY - box.top - box.height / 2 - baseY) / baseK,
        screenX: event.clientX,
        screenY: event.clientY
      };
      // Firefox отдаёт колесо в строках, а не в пикселях, поэтому дельту приводим к общему масштабу.
      const delta = event.deltaMode === 1 ? event.deltaY * 16 : event.deltaMode === 2 ? event.deltaY * 400 : event.deltaY;
      const nextK = Math.max(0.16, Math.min(6, baseK * Math.exp(-delta * ZOOM_SENSITIVITY)));
      if (reducedMotion) {
        applyAnchoredTransform(nextK);
        zoomAnchor = undefined;
        return;
      }
      zoomTarget = nextK;
      if (zoomFrame === undefined) {
        zoomLastFrame = performance.now();
        zoomFrame = requestAnimationFrame(stepZoom);
      }
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Enter" && stateRef.current.selected) onOpen(stateRef.current.selected.id);
      if (event.key === "Escape") {
        clearSelection();
        draw();
      }
    };

    canvas.addEventListener("pointerdown", onPointerDown);
    canvas.addEventListener("pointermove", onPointerMove);
    canvas.addEventListener("pointerup", onPointerUp);
    canvas.addEventListener("pointercancel", onPointerUp);
    canvas.addEventListener("wheel", onWheel, { passive: false });
    canvas.addEventListener("keydown", onKeyDown);
    // В скрытой вкладке плавание останавливается, чтобы не тратить батарею.
    const onVisibility = () => {
      if (reducedMotion) return;
      if (document.hidden) simulation.alphaTarget(0).stop();
      else simulation.alphaTarget(IDLE_ALPHA).restart();
    };
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      simulation.stop();
      cancelZoom();
      observer.disconnect();
      document.removeEventListener("visibilitychange", onVisibility);
      canvas.removeEventListener("pointerdown", onPointerDown);
      canvas.removeEventListener("pointermove", onPointerMove);
      canvas.removeEventListener("pointerup", onPointerUp);
      canvas.removeEventListener("pointercancel", onPointerUp);
      canvas.removeEventListener("wheel", onWheel);
      canvas.removeEventListener("keydown", onKeyDown);
    };
  }, [visibleGraph, onOpen, reducedMotion]);

  return (
    <section className="graph-stage" aria-label="Глобальный граф Зазеркалья">
      <canvas ref={canvasRef} className="graph-canvas" tabIndex={0} aria-label="Интерактивный граф. Перетаскивайте, масштабируйте и выбирайте ноды." />
      <div className={`graph-filter ${filtersOpen ? "is-open" : ""}`}>
        {!filtersOpen ? (
          <button className="graph-filter-toggle" type="button" onClick={() => setFiltersOpen(true)} aria-expanded="false" aria-controls="graph-filter-panel">
            <SlidersHorizontal size={15} aria-hidden="true" />
            <span>Фильтры</span>
            {hiddenTypeCount > 0 ? <b>{hiddenTypeCount}</b> : null}
          </button>
        ) : (
          <div className="graph-filter-panel" id="graph-filter-panel" role="group" aria-label="Фильтры графа">
            <div className="graph-filter-header">
              <strong>Фильтры</strong>
              <div className="graph-filter-actions">
                <button className="graph-filter-icon" type="button" onClick={resetTypes} aria-label="Сбросить фильтры" title="Сбросить фильтры"><RotateCcw size={17} aria-hidden="true" /></button>
                <button className="graph-filter-icon" type="button" onClick={() => setFiltersOpen(false)} aria-label="Свернуть фильтры" title="Свернуть"><X size={18} aria-hidden="true" /></button>
              </div>
            </div>
            <div className="graph-filter-section">
              <div className="graph-filter-section-title">Типы узлов</div>
              <div className="graph-filter-options">
                {typeOptions.map((type) => (
                  <label className="graph-filter-option" key={type}>
                    <input type="checkbox" checked={visibleTypes.has(type)} onChange={() => toggleType(type)} />
                    <span className="graph-filter-swatch" style={{ backgroundColor: typeColor(type) }} aria-hidden="true" />
                    <span className="graph-filter-label">{typeLabel(type)}</span>
                    <small>{graph.nodes.filter((node) => typeKey(node) === type).length}</small>
                  </label>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </section>
  );
}
